import React, { useEffect, useState, createContext, useContext } from 'react';
import { translations, Language } from './translations';
const NON_DEFAULT_LANGUAGES: Language[] = ['ka'];
type LanguageContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => any;
  getLink: (path: string) => string;
};
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
export function LanguageProvider({
  children
}: {
  children: ReactNode;
}) {
  const [language, setLanguageState] = useState<Language>('en');
  useEffect(() => {
    const path = window.location.pathname;
    const pathParts = path.split('/');
    const firstSegment = pathParts[1] as Language;
    if (NON_DEFAULT_LANGUAGES.includes(firstSegment)) {
      // URL like /ua/, /ru/specialists, /ka/companies
      setLanguageState(firstSegment);
    } else {
      // No language prefix or /en/ prefix → English
      // If someone visits /en/ or /en/specialists, redirect to clean URL
      if (firstSegment === 'en') {
        const restOfPath = pathParts.slice(2).filter(Boolean).join('/');
        const cleanPath = restOfPath ? `/${restOfPath}` : '/';
        window.history.replaceState(null, '', cleanPath);
      }
      setLanguageState('en');
    }
  }, []);
  const setLanguage = (lang: Language) => {
    const path = window.location.pathname;
    const pathParts = path.split('/').filter(Boolean);
    // Determine current page (strip language prefix if present)
    let currentPage = '';
    const firstSegment = pathParts[0] as Language;
    if (NON_DEFAULT_LANGUAGES.includes(firstSegment) || firstSegment === 'en') {
      // Has language prefix — page is the rest
      currentPage = pathParts.slice(1).join('/');
    } else {
      // No language prefix (English) — entire path is the page
      currentPage = pathParts.join('/');
    }
    // Build new path
    let newPath: string;
    if (lang === 'en') {
      // English: no prefix
      newPath = currentPage ? `/${currentPage}` : '/';
    } else {
      // Other languages: add prefix
      newPath = currentPage ? `/${lang}/${currentPage}` : `/${lang}/`;
    }
    window.history.pushState(null, '', newPath);
    setLanguageState(lang);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };
  const getLink = (path: string): string => {
    if (language === 'en') {
      return path; // e.g., '/', '/specialists', '/companies'
    }
    return `/${language}${path}`; // e.g., '/ua/', '/ua/specialists'
  };
  const t = (key: string) => {
    const keys = key.split('.');
    let value: any = translations[language];
    for (const k of keys) {
      if (value != null && value[k] !== undefined) {
        value = value[k];
      } else {
        // Fallback to English if key missing
        let fallback: any = translations['en'];
        for (const fk of keys) {
          if (fallback != null && fallback[fk] !== undefined) {
            fallback = fallback[fk];
          } else {
            return key;
          }
        }
        return fallback;
      }
    }
    return value;
  };
  return <LanguageContext.Provider value={{
    language,
    setLanguage,
    t,
    getLink
  }} data-id="element-595">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}